// src/hooks/useProducts.js
import { useQuery, useMutation, useQueryClient } from "react-query";
import { fetchData } from "../api/client";

/** util simples p/ montar querystring */
const toQS = (obj = {}) => {
  const sp = new URLSearchParams();
  Object.entries(obj).forEach(([k, v]) => {
    if (v === null || v === undefined) return;
    const s = String(v).trim();
    if (!s) return;
    sp.set(k, s);
  });
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
};

/**
 * Hook para lista e mutações de produtos
 * 4º argumento:
 *  - string => application (retrocompatível)
 *  - object => { application?, ids_projetos? }
 */
export const useProducts = (page = 1, limit = 10, search = "", paramsOrApplication, queryOptions = {}) => {
  const queryClient = useQueryClient();

  const params =
    typeof paramsOrApplication === "string"
      ? { application: paramsOrApplication }
      : (paramsOrApplication || {});

  const { application, ids_projetos, sort_by, sort_dir } = params;

  // Lista paginada
  const query = useQuery(
    [
      "products",
      page,
      limit,
      search,
      application ?? "",
      ids_projetos ?? "",
      sort_by ?? "",
      sort_dir ?? "",
    ],
    () => {
      const qs = toQS({
        page,
        limit,
        search,
        ...(application ? { application } : {}),
        ...(ids_projetos ? { ids_projetos } : {}),
        ...(sort_by && sort_dir ? { sort_by, sort_dir } : {}),
      });
      return fetchData(`/products${qs}`);
    },
    {
      staleTime: 5000,
      refetchOnWindowFocus: false,
      select: (response) => ({
        products: response.data || [],
        pagination: response.pagination || { total: 0, page, limit },
      }),
      ...queryOptions,
    }
  );

  // Criar (POST /products)
  const createMutation = useMutation(
    (product) => fetchData(`/products`, "POST", product),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("products");
        queryClient.invalidateQueries("filter-options");
      },
    }
  );

  // Atualizar (PUT /products/:id)
  const updateMutation = useMutation(
    (product) => {
      if (!product?.id) throw new Error("updateMutation requer product.id");
      return fetchData(`/products/${product.id}`, "PUT", product);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("products");
        queryClient.invalidateQueries("filter-options");
      },
    }
  );

  // Excluir (DELETE /products/:id)
  const deleteMutation = useMutation(
    (id) => fetchData(`/products/${id}`, "DELETE"),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("products");
        queryClient.invalidateQueries("filter-options");
      },
    }
  );

  // Buscar detalhe (GET /products/:id)
  const getById = async (id) => {
    const resp = await fetchData(`/products/${id}`, "GET");
    return resp?.data || null;
  };

  return { ...query, createMutation, updateMutation, deleteMutation, getById };
};

/**
 * Hook para sugestões de aplicações (carrega da API /products/applications)
 * - q: filtro opcional (para typeahead se quiser no futuro)
 * - limit: teto de itens (default 200)
 * - enabled: permite ligar/desligar o fetch
 */
export const useProductApplications = (q = "", limit = 200, enabled = true) => {
  return useQuery(
    ["productApplications", q, limit],
    async () => {
      const qs = toQS({ q, limit });
      const resp = await fetchData(`/products/applications${qs}`, "GET");
      const raw = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
      return raw
        .map((s) => String(s || "").trim())
        .filter(Boolean)
        .map((s) => ({ key: s, label: s, grupo: undefined }));
    },
    { enabled, staleTime: 60_000, refetchOnWindowFocus: false }
  );
};

/**
 * Busca produtos por termo (para autocomplete assíncrono).
 * Só dispara quando searchTerm tem pelo menos 1 caractere e idsProjetos está definido.
 * idsProjetos = projeto do produto em edição; só retorna produtos do mesmo projeto.
 */
export const useProductSearch = (searchTerm, limit = 50, idsProjetos = null) => {
  const term = String(searchTerm || "").trim();
  const proj = idsProjetos != null && idsProjetos !== "" ? String(idsProjetos) : null;
  const enabled = term.length >= 1 && proj != null;

  return useQuery(
    ["products", "search", term, limit, proj],
    () => {
      const qs = toQS({
        search: term,
        limit,
        page: 1,
        ...(proj ? { ids_projetos: proj } : {}),
      });
      return fetchData(`/products${qs}`);
    },
    {
      enabled,
      staleTime: 30_000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );
};
